/**
 * Reducer for OutpaintTab component state management.
 */

import type { ImageResult, OutpaintDirection } from '@/types';

// ============================================================================
// State
// ============================================================================

export interface OutpaintResult {
  images: ImageResult[];
  processingTime: number;
}

export interface OutpaintState {
  imageBase64: string;
  imageFile: File | null;
  prompt: string;
  negativePrompt: string;
  showNegativePrompt: boolean;
  selectedStyles: string[];
  model: string | undefined;
  distances: Record<OutpaintDirection, number>;
  isProcessing: boolean;
  error: string | null;
  result: OutpaintResult | null;
}

export const initialState: OutpaintState = {
  imageBase64: '',
  imageFile: null,
  prompt: '',
  negativePrompt: '',
  showNegativePrompt: false,
  selectedStyles: [],
  model: undefined,
  distances: {
    Left: 128,
    Right: 128,
    Top: 128,
    Bottom: 128,
  },
  isProcessing: false,
  error: null,
  result: null,
};

// ============================================================================
// Actions
// ============================================================================

export type OutpaintAction =
  | { type: 'SET_IMAGE'; payload: { base64: string; file: File } }
  | { type: 'REMOVE_IMAGE' }
  | { type: 'SET_PROMPT'; payload: string }
  | { type: 'SET_NEGATIVE_PROMPT'; payload: string }
  | { type: 'SHOW_NEGATIVE_PROMPT' }
  | { type: 'HIDE_NEGATIVE_PROMPT' }
  | { type: 'SET_SELECTED_STYLES'; payload: string[] }
  | { type: 'SET_MODEL'; payload: string | undefined }
  | { type: 'SET_DISTANCE'; payload: { direction: OutpaintDirection; value: number } }
  | { type: 'SET_ALL_DISTANCES'; payload: Record<OutpaintDirection, number> }
  | { type: 'SET_PROCESSING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_RESULT'; payload: OutpaintResult | null }
  | { type: 'RESET_RESULT' };

// ============================================================================
// Reducer
// ============================================================================

export function outpaintReducer(state: OutpaintState, action: OutpaintAction): OutpaintState {
  switch (action.type) {
    case 'SET_IMAGE':
      return {
        ...state,
        imageBase64: action.payload.base64,
        imageFile: action.payload.file,
        error: null,
      };

    case 'REMOVE_IMAGE':
      return {
        ...state,
        imageBase64: '',
        imageFile: null,
      };

    case 'SET_PROMPT':
      return { ...state, prompt: action.payload };

    case 'SET_NEGATIVE_PROMPT':
      return { ...state, negativePrompt: action.payload };

    case 'SHOW_NEGATIVE_PROMPT':
      return { ...state, showNegativePrompt: true };

    case 'HIDE_NEGATIVE_PROMPT':
      return { ...state, showNegativePrompt: false, negativePrompt: '' };

    case 'SET_SELECTED_STYLES':
      return { ...state, selectedStyles: action.payload };

    case 'SET_MODEL':
      return { ...state, model: action.payload };

    case 'SET_DISTANCE':
      return {
        ...state,
        distances: {
          ...state.distances,
          [action.payload.direction]: action.payload.value,
        },
      };

    case 'SET_ALL_DISTANCES':
      return { ...state, distances: action.payload };

    case 'SET_PROCESSING':
      return { ...state, isProcessing: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload };

    case 'SET_RESULT':
      return { ...state, result: action.payload };

    case 'RESET_RESULT':
      return { ...state, result: null, error: null };

    default:
      return state;
  }
}
